import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { tokens } from "../../theme";

/**
 * Gráficos del sistema.
 *
 * Son SVG/CSS propios, sin librería: el sistema ya tiene un bundle grande y
 * estos dos casos no justifican otra dependencia. Siguen las reglas del sistema
 * de diseño: una sola serie (sin leyenda, el título la nombra), un solo eje,
 * marcas finas con la punta redondeada, ejes discretos y tooltip al pasar.
 * El color es el rojo de marca, validado contra las superficies clara y oscura.
 */

const SERIES = "var(--sup-brand)";

const Tooltip = ({ label, value, x, align = "center" }) => (
  <Box
    role="tooltip"
    sx={{
      position: "absolute",
      bottom: "calc(100% + 8px)",
      left: x,
      transform:
        align === "center" ? "translateX(-50%)" : align === "left" ? "none" : "translateX(-100%)",
      zIndex: 4,
      pointerEvents: "none",
      whiteSpace: "nowrap",
      padding: "7px 10px",
      borderRadius: "8px",
      backgroundColor: tokens.graphite,
      color: "#fff",
      boxShadow: tokens.shadowMd,
    }}
  >
    <Typography sx={{ fontSize: "11px", color: tokens.graphiteInk2 }}>{label}</Typography>
    <Typography
      sx={{ fontSize: "13px", fontWeight: 600, fontVariantNumeric: "tabular-nums", color: "#fff" }}
    >
      {value}
    </Typography>
  </Box>
);

/**
 * Serie temporal en columnas. `data`: [{ key, label, value, display }].
 * Se etiqueta sólo la barra más alta: un número sobre cada columna es ruido.
 */
export const TimeBars = ({ data = [], height = 190, emptyMessage = "Sin datos en el período" }) => {
  const [hovered, setHovered] = useState(null);

  if (data.length === 0) {
    return (
      <Box sx={{ height, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ fontSize: "13px", color: tokens.ink3 }}>{emptyMessage}</Typography>
      </Box>
    );
  }

  const max = Math.max(...data.map((d) => d.value), 0) || 1;
  const peakIndex = data.reduce((best, d, i) => (d.value > data[best].value ? i : best), 0);

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      {/* Guías discretas: sólo tres, para no competir con las barras. */}
      <Box sx={{ position: "relative", height, width: "100%" }}>
        {[0, 0.5, 1].map((ratio) => (
          <Box
            key={ratio}
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: `${ratio * 100}%`,
              borderTop: `1px ${ratio === 0 ? "solid" : "dashed"} ${tokens.borderSoft}`,
            }}
          />
        ))}

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "flex-end",
            gap: "3px",
          }}
        >
          {data.map((item, index) => {
            const ratio = item.value / max;
            const isHovered = hovered === index;
            return (
              <Box
                key={item.key}
                onMouseEnter={() => setHovered(index)}
                onMouseLeave={() => setHovered(null)}
                sx={{
                  position: "relative",
                  flex: "1 1 0",
                  height: "100%",
                  display: "flex",
                  alignItems: "flex-end",
                  cursor: "default",
                }}
              >
                {isHovered && (
                  <Tooltip label={item.label} value={item.display} x="50%" align="center" />
                )}

                {index === peakIndex && item.value > 0 && !isHovered && (
                  <Typography
                    sx={{
                      position: "absolute",
                      bottom: `calc(${Math.max(ratio * 100, 2)}% + 6px)`,
                      left: "50%",
                      transform: "translateX(-50%)",
                      fontSize: "10.5px",
                      fontWeight: 600,
                      fontVariantNumeric: "tabular-nums",
                      color: tokens.ink2,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.display}
                  </Typography>
                )}

                <Box
                  sx={{
                    width: "100%",
                    height: `${Math.max(ratio * 100, item.value > 0 ? 2 : 0)}%`,
                    minHeight: item.value > 0 ? 3 : 0,
                    borderRadius: "4px 4px 0 0",
                    backgroundColor: SERIES,
                    opacity: hovered === null || isHovered ? 1 : 0.55,
                    transition: "opacity 120ms ease",
                  }}
                />
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: "3px", marginTop: "7px" }}>
        {data.map((item) => (
          <Typography
            key={item.key}
            sx={{
              flex: "1 1 0",
              textAlign: "center",
              fontSize: "10.5px",
              color: tokens.ink3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.short || item.label}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

/**
 * Ranking en barras horizontales. `data`: [{ key, label, value, display, meta }].
 * Acá sí se etiqueta cada barra: son pocas filas y el número es el dato.
 */
export const RankBars = ({ data = [], emptyMessage = "Sin datos en el período" }) => {
  const [hovered, setHovered] = useState(null);

  if (data.length === 0) {
    return (
      <Box sx={{ padding: "28px 0", textAlign: "center" }}>
        <Typography sx={{ fontSize: "13px", color: tokens.ink3 }}>{emptyMessage}</Typography>
      </Box>
    );
  }

  const max = Math.max(...data.map((d) => d.value), 0) || 1;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%" }}>
      {data.map((item, index) => (
        <Box
          key={item.key}
          onMouseEnter={() => setHovered(index)}
          onMouseLeave={() => setHovered(null)}
          sx={{ display: "flex", flexDirection: "column", gap: "5px", minWidth: 0 }}
        >
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "10px", minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "12.5px",
                fontWeight: 500,
                flexGrow: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: tokens.ink,
              }}
              title={item.label}
            >
              {item.label}
            </Typography>
            {item.meta && (
              <Typography sx={{ fontSize: "11px", color: tokens.ink3, whiteSpace: "nowrap" }}>
                {item.meta}
              </Typography>
            )}
            <Typography
              sx={{
                fontSize: "12.5px",
                fontWeight: 600,
                fontVariantNumeric: "tabular-nums",
                whiteSpace: "nowrap",
                color: tokens.ink,
              }}
            >
              {item.display}
            </Typography>
          </Box>

          <Box
            sx={{
              position: "relative",
              height: 8,
              borderRadius: 999,
              backgroundColor: tokens.borderSoft,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${Math.max((item.value / max) * 100, item.value > 0 ? 1.5 : 0)}%`,
                borderRadius: 999,
                backgroundColor: SERIES,
                opacity: hovered === null || hovered === index ? 1 : 0.55,
                transition: "opacity 120ms ease",
              }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default { TimeBars, RankBars };
